/**
 * @fileoverview Sparkle trail effect for dragged shapes
 * Creates a particle trail that follows the dragged shape
 */

import Konva from 'konva';

interface Sparkle {
    shape: Konva.Star;
    velocity: { x: number; y: number };
    life: number;
    maxLife: number;
}

/**
 * Manages sparkle particle effects that trail behind dragged shapes
 */
export class SparkleTrail {
    private sparkles: Sparkle[] = [];
    private layer: Konva.Layer;
    private animationFrame: number | null = null;
    private isActive: boolean = false;

    constructor(layer: Konva.Layer) {
        this.layer = layer;
    }

    /**
     * Start emitting sparkles at the given position
     */
    start(x: number, y: number, color: string = '#FFFF00') {
        this.isActive = true;
        this.emit(x, y, color);
        
        if (!this.animationFrame) {
            this.animate();
        }
    }

    /**
     * Update sparkle position during drag
     */
    update(x: number, y: number, color: string = '#FFFF00') {
        if (this.isActive) {
            this.emit(x, y, color);
        }
    }

    /**
     * Stop emitting new sparkles
     */
    stop() {
        this.isActive = false;
    }

    /**
     * Emit a new sparkle particle
     */
    private emit(x: number, y: number, color: string) {
        // Don't emit if layer is not available
        if (!this.layer) {
            console.warn('SparkleTrail: layer is null');
            return;
        }
        
        // Create 2-3 sparkles per emission
        const count = Math.floor(Math.random() * 2) + 2;
        
        for (let i = 0; i < count; i++) {
            const sparkle = new Konva.Star({
                x: x + (Math.random() - 0.5) * 20,
                y: y + (Math.random() - 0.5) * 20,
                numPoints: 4,
                innerRadius: 2,
                outerRadius: 5 + Math.random() * 3,
                fill: color,
                opacity: 0.8,
                rotation: Math.random() * 360,
            });

            this.layer.add(sparkle);
            // Move sparkle behind the dragged shape
            sparkle.moveToBottom();
            
            // console.log(`Created sparkle at (${sparkle.x()}, ${sparkle.y()}) with color ${color}`);

            this.sparkles.push({
                shape: sparkle,
                velocity: {
                    x: (Math.random() - 0.5) * 2,
                    y: (Math.random() - 0.5) * 2,
                },
                life: 1,
                maxLife: 0.5 + Math.random() * 0.5, // 0.5 to 1 second
            });
        }
    }

    /**
     * Animate all sparkles
     */
    private animate() {
        //console.log(`Animating sparkles: ${this.sparkles.length} active`);
        const deltaTime = 1 / 60; // Assume 60fps

        // Update each sparkle
        for (let i = this.sparkles.length - 1; i >= 0; i--) {
            const sparkle = this.sparkles[i];
            
            // Update position
            sparkle.shape.x(sparkle.shape.x() + sparkle.velocity.x);
            sparkle.shape.y(sparkle.shape.y() + sparkle.velocity.y);
            
            // Update life and fade out
            sparkle.life -= deltaTime;
            const fadeRatio = sparkle.life / sparkle.maxLife;
            sparkle.shape.opacity(fadeRatio * 0.8);
            sparkle.shape.scaleX(fadeRatio);
            sparkle.shape.scaleY(fadeRatio);
            
            // Rotate sparkle
            sparkle.shape.rotation(sparkle.shape.rotation() + 5);
            
            // Remove dead sparkles
            if (sparkle.life <= 0) {
                sparkle.shape.destroy();
                this.sparkles.splice(i, 1);
            }
        }

        if (this.layer) {
            this.layer.batchDraw();
        }

        // Continue animation if there are sparkles or still active
        if (this.sparkles.length > 0 || this.isActive) {
            this.animationFrame = requestAnimationFrame(() => this.animate());
        } else {
            this.animationFrame = null;
        }
    }

    /**
     * Clear all sparkles
     */
    clear() {
        this.isActive = false;
        
        for (const sparkle of this.sparkles) {
            sparkle.shape.destroy();
        }
        
        this.sparkles = [];
        
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        
        if (this.layer) {
            this.layer.batchDraw();
        }
    }
}
